import type { Prompt } from "@/context/prompt"

export const dashboardWidgetMentionPrefix = "__datox_widget__:"

export type DashboardWidgetComposerContext = {
  sessionID: string
  dashboard: { id: string; title: string }
  tabs: Array<{
    id: string
    title: string
    widgets: Array<{
      id: string
      title: string
      type: string
      definition: Record<string, unknown>
      datasetID?: string
    }>
  }>
  tables: Record<string, {
    name: string
    headers: string[]
    rows: Array<Record<string, unknown>>
    totalRows: number
    truncated: boolean
  }>
}

export function dashboardWidgetMentionName(dashboardID: string, tabID: string, widgetID: string) {
  return `${dashboardWidgetMentionPrefix}${[dashboardID, tabID, widgetID].map(encodeURIComponent).join(":")}`
}

export function parseDashboardWidgetMention(name: string) {
  if (!name.startsWith(dashboardWidgetMentionPrefix)) return
  const values = name.slice(dashboardWidgetMentionPrefix.length).split(":").map(decodeURIComponent)
  if (values.length !== 3 || values.some((value) => !value)) return
  return { dashboardID: values[0], tabID: values[1], widgetID: values[2] }
}

export function dashboardWidgetSuggestions(context?: DashboardWidgetComposerContext) {
  if (!context) return []
  return context.tabs.flatMap((tab) => tab.widgets.map((widget) => ({
    id: `widget:${context.dashboard.id}:${tab.id}:${widget.id}`,
    label: `#${widget.title}`,
    description: `${context.dashboard.title} / ${tab.title}`,
    mentionName: dashboardWidgetMentionName(context.dashboard.id, tab.id, widget.id),
  })))
}

export function dashboardWidgetSyntheticContext(context: DashboardWidgetComposerContext | undefined, mentionName: string) {
  const identity = parseDashboardWidgetMention(mentionName)
  if (!context || !identity || identity.dashboardID !== context.dashboard.id) return
  const tab = context.tabs.find((item) => item.id === identity.tabID)
  const widget = tab?.widgets.find((item) => item.id === identity.widgetID)
  if (!tab || !widget) return
  return JSON.stringify({
    type: "datox_dashboard_widget",
    dashboard: context.dashboard,
    tab: { id: tab.id, title: tab.title },
    widget: {
      id: widget.id,
      title: widget.title,
      type: widget.type,
      definition: widget.definition,
    },
    data: widget.datasetID ? context.tables[widget.datasetID] : undefined,
  })
}

export function transformDashboardWidgetMentions(prompt: Prompt, context?: DashboardWidgetComposerContext) {
  const synthetic: Prompt = []
  const visible = prompt.flatMap((part): Prompt => {
    if (part.type !== "agent" || !parseDashboardWidgetMention(part.name)) return [part]
    const value = dashboardWidgetSyntheticContext(context, part.name)
    if (value) {
      const content = `Datox dashboard widget context:\n${value}`
      synthetic.push({ type: "text", content, start: 0, end: content.length, synthetic: true })
    }
    return [{ type: "text", content: part.content, start: part.start, end: part.end }]
  })
  return { prompt: visible, context: synthetic }
}
